import { Trans, useTranslation } from "@i18n";
import { useEffect, useRef, useState } from "react";
import AutoComplete from "@CubeComponents/Mui_Form/AutoComplete";
import { ManualMeasurementState, manualCostSources, unitTypesList } from "@Actions/estimation";
import axiosWrapper from "@AxiosWrapper";
import { portalMeasurementUnitsUrl } from "@Services/dataManager";
import type { GetMeasurementUnitsResponse } from "@ServerTypes/units/units.schema";
import NumericTextBoxComponent from "@CubeComponents/Mui_Form/NumericTextBoxComponent";
import _ from "lodash";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import Text from "@CubeComponents/html/Text/Text";
import CentralManualCostComponent from "./CentralManualCostComponent";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import type { ObjectIdString } from "@Helpers/schemas/common.schema";

type ManualMeasurementProps = {
  measurementData?: ManualMeasurementState;
  handleCloseManualMeasurement: () => void;
  estimateId: ObjectIdString;
  handleCreateManualMeasurement: (
    data: ManualMeasurementState,
    previous?: ManualMeasurementState,
  ) => Promise<void> | void;
  fileVersionId?: string;
};

const ManualMeasurement = ({
  measurementData,
  handleCloseManualMeasurement,
  handleCreateManualMeasurement,
  estimateId,
  fileVersionId,
}: ManualMeasurementProps) => {
  const { t } = useTranslation();
  const defaultState: ManualMeasurementState = {
    name: "",
    type: "",
    unit: "",
    value: null,
    references: [],
    isReferenced: false,
    formula: "",
  };
  const [manualData, setManualData] = useState<ManualMeasurementState>(measurementData || defaultState);
  const [measurementUnitsOptions, setMeasurementUnitsOptions] = useState<
    NonNullable<GetMeasurementUnitsResponse["result"]>
  >([]);

  const unitRef = useRef<{ value: string } | null>(null);

  useEffect(() => {
    const fetchMeasurementUnits = async () => {
      if (manualData.type) {
        const response = await axiosWrapper.post<GetMeasurementUnitsResponse>(portalMeasurementUnitsUrl, {
          value: { params: { type: manualData.type } },
        });
        if (response.data.result) {
          setMeasurementUnitsOptions(response.data.result);
        }
      }
    };
    fetchMeasurementUnits();
  }, [manualData.type]);

  const typeDropdown = (disabled?: boolean) => (
    <AutoComplete
      options={unitTypesList}
      valueField="name"
      getOptionLabel={(option) => option.name || ""}
      value={manualData.type}
      disabled={disabled}
      onChange={(_event, newValue) => {
        setManualData((prev) => ({ ...prev, type: newValue?.name || "", unit: "" }));
        if (unitRef.current) {
          unitRef.current.value = "";
        }
      }}
      renderInput={(params) => <TextFieldComponent {...params} placeholder={t("Type")} />}
    />
  );

  const content = () => (
    <div className="flex flex-col gap-6">
      <div className="flex w-full gap-5">
        <div className="flex flex-col flex-1 gap-3">
          <Text className="text-sm font-semibold">{t("Name")}</Text>
          <TextFieldComponent
            value={manualData.name}
            placeholder={t("Measurement Name")}
            onChange={(e: { target: { value: string } }) =>
              setManualData((prev) => ({ ...prev, name: e.target.value }))
            }
          />
        </div>
        <div className="flex flex-col flex-1 gap-3 justify-between">
          <Text className="text-sm font-semibold">{t("Type")}</Text>
          {measurementData && measurementData.takeOffs?.length ? (
            <ToolTipComponent
              position="bottom"
              content={t("Editing type is not allowed since the measurement is already a part of certain takeoff(s)")}
            >
              {typeDropdown(true)}
            </ToolTipComponent>
          ) : (
            typeDropdown()
          )}
        </div>
      </div>
      <div className="flex w-full gap-6">
        <div className="flex flex-col flex-1 gap-3">
          <Text className="text-sm font-semibold">{t("Value")}</Text>
          <NumericTextBoxComponent
            showSpinButton={false}
            enabled={!manualData.isReferenced}
            value={manualData.value ?? undefined}
            placeholder={manualData.isReferenced ? t("Calculated Value") : t("Add Value")}
            onChange={(e: { target: { value: string } }) =>
              setManualData((prev) => ({ ...prev, value: Number.parseFloat(e.target.value) }))
            }
            step={1}
          />
        </div>

        <div className="flex flex-col flex-1 gap-3 justify-between">
          <Text className="text-sm font-semibold">{t("Unit")}</Text>
          <AutoComplete
            options={measurementUnitsOptions}
            valueField="_id"
            getOptionLabel={(option) => option.selectedSymbol?.name || option.name || ""}
            value={manualData.unit}
            disabled={!manualData.type}
            onChange={(_event, newValue) => setManualData((prev) => ({ ...prev, unit: newValue?._id || "" }))}
            renderInput={(params) => <TextFieldComponent {...params} placeholder={t("Unit")} />}
          />
        </div>
      </div>
    </div>
  );

  const saveClickHadler = async () => {
    await handleCreateManualMeasurement(manualData, measurementData);
  };

  const isSaveButtonDisabled = !(
    manualData.name.trim() &&
    manualData.type &&
    Number.isFinite(manualData.value) &&
    manualData.unit
  );

  return (
    <CentralManualCostComponent
      estimateId={estimateId}
      fileVersionId={fileVersionId}
      initialData={measurementData}
      manualData={manualData}
      setManualData={setManualData}
      headerText={measurementData ? t("Edit Manual Measurement") : t("Create Manual Measurement")}
      footerConfirmText={measurementData ? t("Save") : t("Create")}
      footerCancelText={t("Discard")}
      upperContent={content}
      referenceToggleLabel={t("Reference Existing Measurements")}
      noReferenceHeading1={<Text sx={{ whiteSpace: "normal" }}>{t("No Reference Measurements")} </Text>}
      noReferenceHeading2={
        <Text sx={{ whiteSpace: "normal" }}>
          <Trans
            i18nKey="add_reference_text"
            components={{ bold: <span className="font-bold" /> }}
            values={{ name: "measurements" }}
          />
        </Text>
      }
      addReferenceHeaderText={t("Add reference measurements")}
      formulaDetailToolTip={t("manualMeasurement_formula_icon_tooltip")}
      handleSave={saveClickHadler}
      handleCancel={handleCloseManualMeasurement}
      isSaveButtonDisabled={isSaveButtonDisabled}
      source={manualCostSources.MEASUREMENTS}
    />
  );
};
export default ManualMeasurement;
